import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api, { isRequestAborted } from './api.js';

const OfferContext = createContext({
  level: 1,
  modules: [],
  features: {},
  defaults: {},
  ready: false,
  manifest: null,
  theme: {},
});

const FALLBACK_MANIFEST = { level: 1, hash: 'not-yet-activated', features: {}, defaults: {}, modules: [] };
const FALLBACK_CLIENT = {
  theme: { primary: '#C1835E', secondary: '#E4986D', logo: '', displayName: 'Happy Paws' },
  client: { name: 'Starter Client', timezone: 'Europe/Paris', locale: 'fr_FR' },
};

/**
 * Boots by loading the build-time manifest (derived by activate-level.php),
 * then refreshes it from /api/v1/manifest at runtime.
 */
export default function OfferProvider({ children }) {
  const [state, setState] = useState({ ...FALLBACK_MANIFEST, ready: false, manifest: FALLBACK_MANIFEST });
  const [client, setClient] = useState(FALLBACK_CLIENT);

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([
      import('./offer.manifest.json').catch(() => ({ default: FALLBACK_MANIFEST })),
      import('@theme/client.generated.js').catch(() => ({ default: FALLBACK_CLIENT })),
    ]).then(([manifestRes, clientRes]) => {
      if (cancelled) return;
      const manifest = manifestRes.status === 'fulfilled' ? (manifestRes.value.default || manifestRes.value) : FALLBACK_MANIFEST;
      const cl = clientRes.status === 'fulfilled' ? (clientRes.value.default || clientRes.value) : FALLBACK_CLIENT;
      setState({
        level: manifest.level ?? 1,
        modules: manifest.modules || [],
        features: manifest.features || {},
        defaults: manifest.defaults || {},
        manifest,
        ready: true,
      });
      setClient({ ...FALLBACK_CLIENT, ...cl });
    });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!state.ready) return;
    const ac = new AbortController();
    api.get('/v1/manifest', { signal: ac.signal }).then(({ data }) => {
      if (!data?.hash) return;
      setState((prev) => ({
        ...prev,
        level: data.level,
        modules: data.modules || [],
        features: data.features || {},
        defaults: data.defaults || {},
        manifest: data,
      }));
    }).catch((error) => {
      if (isRequestAborted(error)) return;
      window.dispatchEvent(new CustomEvent('starter:api-warning', {
        detail: { source: 'manifest', message: error?.message || 'Manifest refresh failed.' },
      }));
    });
    return () => ac.abort();
  }, [state.ready]);

  const value = useMemo(
    () => ({ ...state, theme: client.theme, client: client.client }),
    [state, client],
  );

  return <OfferContext.Provider value={value}>{children}</OfferContext.Provider>;
}

export function useOffer() {
  return useContext(OfferContext);
}

export function useFeature(flag, defaultValue = false) {
  const { features } = useOffer();
  return features[flag] ?? defaultValue;
}

export function useModuleEnabled(name) {
  const { modules } = useOffer();
  return !!modules.find((m) => m.name === name);
}
